/**
 * When the queue is flushed (D-10 "Sync", `TRACK-D` "Sync triggers").
 *
 * Triggers, all of them:
 *
 * - app start
 * - the `online` event
 * - `visibilitychange` → visible — **the important one on iOS**, which has no
 *   Background Sync and no Web Push, so foreground sync is the only sync
 * - after any successful online request (`notifyServerContact`, wired to the
 *   clock anchor, which every response already updates)
 * - a retry timer: 5 s → 15 s → 60 s → 5 min, capped
 *
 * Two rules hold across all of them: **never two flushes at once** (the thunk
 * is single-flight and this adds a second gate), and **no timer while
 * offline** — a retry that cannot reach the network only wastes battery on a
 * phone that is already in a basement.
 */
import { onServerContact } from './clock';
import { onlineChanged } from './queueSlice';
import { syncQueue, type OfflineDispatch, type SyncOutcome } from './thunks';

export const RETRY_DELAYS_MS: readonly number[] = [5_000, 15_000, 60_000, 300_000];

export type SyncTrigger = 'start' | 'online' | 'foreground' | 'retry' | 'server-contact' | 'manual';

export interface OfflineSchedulerOptions {
  /** Override the flush itself. Production passes nothing. */
  readonly flush?: (trigger: SyncTrigger) => Promise<SyncOutcome>;
  /**
   * `false` short-circuits the cheap triggers when the mirror says there is
   * nothing to send. Wire it to `state.offlineQueue.pending.length > 0`.
   */
  readonly hasPending?: () => boolean;
}

export interface OfflineScheduler {
  /** Flush now if nothing else is flushing. Fire and forget. */
  readonly flushNow: (trigger: SyncTrigger) => void;
  /** Remove every listener and cancel the retry timer. */
  readonly stop: () => void;
  /** Test seam: the delay currently armed, or `null`. */
  readonly pendingDelayMs: () => number | null;
}

function delayFor(attempt: number): number {
  const last = RETRY_DELAYS_MS[RETRY_DELAYS_MS.length - 1] ?? 300_000;
  return RETRY_DELAYS_MS[Math.min(attempt, RETRY_DELAYS_MS.length - 1)] ?? last;
}

export function startOfflineScheduler(
  dispatch: OfflineDispatch,
  options: OfflineSchedulerOptions = {},
): OfflineScheduler {
  const flush = options.flush ?? ((): Promise<SyncOutcome> => dispatch(syncQueue()).unwrap());
  const hasPending = options.hasPending;

  let running = false;
  let stopped = false;
  let attempt = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let armedDelay: number | null = null;

  const cancelTimer = (): void => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
    armedDelay = null;
  };

  const arm = (): void => {
    cancelTimer();
    if (stopped) return;
    const delay = delayFor(attempt);
    attempt += 1;
    armedDelay = delay;
    timer = setTimeout(() => {
      timer = null;
      armedDelay = null;
      run('retry');
    }, delay);
  };

  const online = (): boolean =>
    typeof navigator === 'undefined' ? true : navigator.onLine !== false;

  function run(trigger: SyncTrigger): void {
    if (stopped || running) return;
    // Cheap triggers do not wake IndexedDB when the mirror says it is empty.
    if (trigger === 'server-contact' && hasPending?.() === false) return;
    if (!online()) {
      cancelTimer();
      return;
    }
    running = true;
    void flush(trigger)
      .then((outcome) => {
        if (outcome.skipped === 'offline') {
          cancelTimer();
          return;
        }
        if (outcome.retried > 0 || outcome.error !== null) {
          arm();
          return;
        }
        // A clean pass (including "nothing to send") resets the backoff.
        attempt = 0;
        cancelTimer();
      })
      .catch(() => {
        arm();
      })
      .finally(() => {
        running = false;
      });
  }

  const onOnline = (): void => {
    dispatch(onlineChanged(true));
    attempt = 0;
    run('online');
  };

  const onOffline = (): void => {
    dispatch(onlineChanged(false));
    cancelTimer();
  };

  const onVisibility = (): void => {
    if (typeof document === 'undefined') return;
    if (document.visibilityState === 'visible') run('foreground');
  };

  if (typeof window !== 'undefined') {
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
  }
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibility);
  }

  // Every successful response is also a sync trigger: a punch queued while the
  // network was down leaves as soon as anything else succeeds, without waiting
  // for the next backoff step.
  const unsubscribeServerContact = onServerContact(() => {
    run('server-contact');
  });

  const scheduler: OfflineScheduler = {
    flushNow: (trigger) => {
      run(trigger);
    },
    stop: () => {
      stopped = true;
      cancelTimer();
      unsubscribeServerContact();
      if (typeof window !== 'undefined') {
        window.removeEventListener('online', onOnline);
        window.removeEventListener('offline', onOffline);
      }
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibility);
      }
    },
    pendingDelayMs: () => armedDelay,
  };

  run('start');
  return scheduler;
}
